import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiShieldLockOutline } from '@mdi/js'
import type { DisclosureEvent } from '../../api/schema'
import { useCan } from '../../contexts/PermissionsContext'
import { useDisclosures, useDocumentSubjects } from '../../hooks/useDataProtection'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { Badge } from '../ui/badge'
import { AppIcon } from '../ui/icon'

/** Banner of a document under a GDPR restriction (spec 2026-10-03-02, lot 2). */
export function DocumentRestrictionBanner() {
    const { t } = useTranslation('data-protection')
    return (
        <Alert variant="warning" className="mb-4" data-testid="document-restriction-banner">
            <AppIcon path={mdiShieldLockOutline} size="sm" />
            <AlertTitle>{t('restricted.banner_title')}</AlertTitle>
            <AlertDescription>{t('restricted.banner_body')}</AlertDescription>
        </Alert>
    )
}

/**
 * The persons a document concerns, with their origin, and its disclosures
 * (the "Diffusions" of spec 2026-10-03-02). Shown to `data_protection.read`.
 */
export function DocumentDataProtection({ org, docUid }: { org: string; docUid: string }) {
    const { t } = useTranslation('data-protection')
    const canRead = useCan('data_protection.read')
    const subjects = useDocumentSubjects(org, docUid, canRead)
    const disclosures = useDisclosures(org, { document_uid: docUid }, canRead)
    if (!canRead) return null

    return (
        <section className="mt-6 border rounded-lg p-4" data-testid="document-data-protection">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('document.title')}</h2>

            <h3 className="text-sm font-medium mb-1">{t('document.subjects')}</h3>
            {subjects.data?.length ? (
                <ul className="mb-4 space-y-1" data-testid="document-subjects">
                    {subjects.data.map((s) => (
                        <li key={s.uid} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`document-subject-${s.uid}`}>
                            <Link to="/$org/users/$userUid" params={{ org, userUid: s.uid }} className="hover:underline" data-testid={`document-subject-link-${s.uid}`}>
                                {s.name}
                            </Link>
                            {s.origins.map((o) => (
                                <Badge key={o} variant="secondary">{t(`origin.${o}`)}</Badge>
                            ))}
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="text-sm text-muted-foreground mb-4" data-testid="document-subjects-empty">{t('document.no_subjects')}</p>
            )}

            <h3 className="text-sm font-medium mb-1">{t('disclosures.title')}</h3>
            <DisclosureList query={disclosures} />
        </section>
    )
}

export function DisclosureList({ query }: { query: { data?: DisclosureEvent[]; error: Error | null; isLoading: boolean } }) {
    const { t, i18n } = useTranslation('data-protection')
    if (query.error?.message === 'consultation_disabled') {
        return <p className="text-sm text-muted-foreground" data-testid="disclosures-disabled">{t('disclosures.disabled')}</p>
    }
    if (query.isLoading) return null
    if (!query.data?.length) {
        return <p className="text-sm text-muted-foreground" data-testid="disclosures-empty">{t('disclosures.empty')}</p>
    }
    return (
        <>
            <p className="text-xs text-muted-foreground mb-1">{t('disclosures.not_productivity')}</p>
            <ul className="divide-y border rounded-md" data-testid="disclosures-list">
                {query.data.map((e) => (
                    <li key={e.uid} className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-sm" data-testid={`disclosure-${e.uid}`}>
                        <span className="tabular-nums text-muted-foreground">{new Date(e.occurred_at).toLocaleString(i18n.language)}</span>
                        <Badge variant={e.kind === 'withheld' ? 'warning' : 'secondary'}>{t(`disclosures.kind.${e.kind}`)}</Badge>
                        <span>{t(`disclosures.channel.${e.channel}`, { defaultValue: e.channel })}</span>
                        <span className="flex-1 truncate">{e.anonymous ? t('disclosures.anonymous') : e.actor_name}</span>
                        <span className="truncate text-muted-foreground">{e.document_name}</span>
                    </li>
                ))}
            </ul>
        </>
    )
}
